import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { founderApi } from '@/lib/founderApi';
import { errMsg } from '@/lib/digitalApi';
import { usePageMeta } from '@/lib/usePageMeta';
import { useStageAutosave } from '@/hooks/useStageAutosave';
import { FounderFrame } from '@/components/founder/FounderFrame';
import { StageTabs } from '@/components/founder/StageTabs';
import { SaveIndicator } from '@/components/founder/SaveIndicator';
import { ConflictDialog } from '@/components/founder/ConflictDialog';
import { StageSections } from '@/components/founder/StageSections';
import { CompletionBar } from '@/components/founder/CompletionBar';
import { StageSummary } from '@/components/founder/StageSummary';

const ORDER = ['discover', 'define', 'design', 'calculate', 'decide', 'launch'];
const isEmpty = (v) => v === null || v === undefined || (typeof v === 'string' && !v.trim());

export default function StageStudio() {
  const { bid, stage } = useParams();
  usePageMeta({ title: 'Founder Blueprint', path: `/founder/blueprint/${bid}/${stage}`, robots: 'noindex' });
  const navigate = useNavigate();
  const [bp, setBp] = useState(null);
  const [missing, setMissing] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [conflict, setConflict] = useState(false);
  const versionRef = useRef(0);
  const onSaved = useCallback((view) => { versionRef.current = view.version; setBp(view); }, []);
  const autosave = useStageAutosave({ bid, stage, versionRef, onSaved });

  const load = useCallback(() => founderApi.blueprint(bid).then(onSaved).catch((e) => {
    navigate(e?.response?.status === 401 ? '/founder/login' : '/founder/workspace', { replace: true });
  }), [bid, navigate, onSaved]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setMissing([]); window.scrollTo(0, 0); }, [stage]);

  const values = useMemo(() => ({ ...(bp?.answers?.[stage] || {}), ...autosave.dirty }), [bp, stage, autosave.dirty]);
  const all = useMemo(() => (bp ? { ...bp.answers, [stage]: values } : {}), [bp, stage, values]);
  const business = all.discover?.concept_name?.trim() || 'your business';
  const offer = all.design?.offer_name?.trim() || 'your offer';
  const price = all.design?.price ? `$${Number(all.design.price).toLocaleString()}` : 'your price';
  const personalize = useCallback((t) => (t || '').replaceAll('{business}', business).replaceAll('{offer}', offer).replaceAll('{price}', price), [business, offer, price]);

  if (!ORDER.includes(stage)) return <Navigate to="/founder/workspace" replace />;
  if (!bp) return <FounderFrame testid="stage-loading"><Loader2 className="animate-spin text-gold" /></FounderFrame>;

  const schema = bp.schema[stage];
  const meta = bp.stages.find((s) => s.key === stage);
  const required = schema.sections.flatMap((s) => s.fields).filter((f) => f.required);
  const reviewed = (f) => !f.prefill_from || f.key in autosave.dirty || bp.links[`${stage}.${f.key}`]?.customized || bp.links[`${stage}.${f.key}`]?.confirmed;
  const isFilled = (f) => !isEmpty(values[f.key]) && reviewed(f);
  const fieldOf = (k) => schema.sections.flatMap((s) => s.fields).find((f) => f.key === k);
  const liveMissing = missing.filter((m) => !isFilled(fieldOf(m.key)) || (m.key === 'price' && !(values.price > 0)));
  const showConflict = conflict || autosave.status.state === 'conflict';
  const idx = ORDER.indexOf(stage);

  const handleConflict = (e) => { if (e?.response?.status === 409) { setConflict(true); return true; } return false; };
  const resolve = async (keep) => {
    const fresh = await founderApi.blueprint(bid);
    onSaved(fresh);
    setConflict(false);
    if (keep) await autosave.flush(); else autosave.discard();
    toast.success(keep ? 'Your changes were saved on the latest version.' : 'Loaded the latest version.');
  };
  const linkAction = async (key, action) => {
    await autosave.flush();
    try { onSaved(await founderApi.resolveLink(bid, `${stage}.${key}`, action, versionRef.current)); } catch (e) { if (!handleConflict(e)) toast.error(errMsg(e)); }
  };
  const complete = async () => {
    setCompleting(true);
    await autosave.flush();
    try {
      onSaved(await founderApi.completeStage(bid, stage, versionRef.current));
      setMissing([]);
      toast.success(`${meta.name} marked complete. You can return and edit it anytime.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'missing_required') setMissing(d.missing);
      else if (!handleConflict(e)) toast.error(errMsg(e));
    }
    setCompleting(false);
  };

  return (
    <FounderFrame testid="stage-studio-page" wide>
      <Link to="/founder/workspace" onClick={() => autosave.flush()} className="text-[11px] uppercase tracking-[0.2em] text-matte/60 hover:text-matte inline-flex items-center gap-2" data-testid="stage-back-to-workspace">
        <ArrowLeft size={12} /> Founder Workspace
      </Link>
      <div className="mt-6 max-w-full overflow-hidden"><StageTabs bid={bid} stages={bp.stages} statuses={bp.stage_status} current={stage} onNavigate={() => autosave.flush()} /></div>
      <div className="mt-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow !text-gold">Stage {meta.number} · {meta.name}</div>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-matte leading-[1.02]" data-testid="stage-title">{meta.subtitle}</h1>
        </div>
      </div>
      <div className="fixed bottom-6 left-6 z-40 rounded-full bg-white/90 backdrop-blur-md border border-matte/10 shadow-sm px-4 py-2 max-w-[calc(100vw-7rem)]" data-testid="stage-save-pill">
        <SaveIndicator status={autosave.status} onRetry={() => autosave.flush()} />
      </div>
      <p className="mt-6 max-w-3xl text-matte/70 leading-relaxed" data-testid="stage-intro">{personalize(schema.intro)}</p>
      <StageSections schema={schema} stage={stage} values={values} all={all} bp={bp} autosave={autosave}
        missingKeys={new Set(liveMissing.map((m) => m.key))} personalize={personalize} onLinkAction={linkAction} />
      <CompletionBar status={bp.stage_status?.[stage]} total={required.length}
        filled={required.filter(isFilled).length} missing={liveMissing} completing={completing} onComplete={complete} milestone={personalize(schema.milestone)} />
      <StageSummary schema={schema} answers={values} personalize={personalize} />
      <div className="mt-12 flex justify-between gap-4">
        {idx > 0 ? <Link to={`/founder/blueprint/${bid}/${ORDER[idx - 1]}`} onClick={() => autosave.flush()} className="btn-ghost" data-testid="stage-prev-button"><ArrowLeft size={14} /> Previous stage</Link> : <span />}
        {idx < ORDER.length - 1 && <Link to={`/founder/blueprint/${bid}/${ORDER[idx + 1]}`} onClick={() => autosave.flush()} className="btn-primary" data-testid="stage-next-button">Next stage <ArrowRight size={14} /></Link>}
      </div>
      <ConflictDialog open={showConflict} onReload={() => resolve(false)} onKeep={() => resolve(true)} />
    </FounderFrame>
  );
}
